import { useCallback, useRef, useState } from 'react';
import { usePresence } from '../state/presence';
import { screenToWorld, type Viewport } from '../canvas/viewport';
import type { StrokeIndex } from '../canvas/spatialIndex';
import { displayName } from '../utils/name';
import { getCachedUserName, fetchUserName } from '../sync/userNameCache';

export interface HoveredAuthor {
  name: string;
  /** 作者 user_id（管理员渲染 #短ID 副行，靠它搜索定位） */
  userId?: string;
  color: string;
  sx: number;
  sy: number;
}

/**
 * Stroke author hover badge — hit-tests the spatial index on hover and resolves
 * user_id via the presence map. Extracted from Board.tsx to keep the route
 * component under its line budget (R4-P1-2 / R5-A).
 */
export function useStrokeAuthorHover() {
  const [hoveredAuthor, setHoveredAuthor] = useState<HoveredAuthor | null>(null);
  const pendingRef = useRef<{ userId: string; at: number; fired: boolean } | null>(null);

  const setBadge = useCallback((name: string, color: string, sx: number, sy: number, userId?: string) => {
    setHoveredAuthor((prev) => {
      if (prev && prev.name === name && prev.userId === userId && Math.abs(prev.sx - sx) < 8 && Math.abs(prev.sy - sy) < 8) {
        return prev;
      }
      return { name, color, sx, sy, userId };
    });
  }, []);

  const updateHover = useCallback(
    (
      e: { clientX: number; clientY: number; pointerType: string },
      viewport: Viewport,
      index: StrokeIndex,
      isDragging: boolean,
      isAdminEraseActive: boolean,
    ) => {
      if (isDragging || e.pointerType === 'touch' || isAdminEraseActive) {
        setHoveredAuthor(null);
        return;
      }
      const [wx, wy] = screenToWorld(viewport, e.clientX, e.clientY);
      // 精确几何拾取：bbox 命中会在空白处误弹作者徽章（长对角线笔画 bbox 很大）
      const top = index.pickStrokeAt(wx, wy, 8 / viewport.zoom, () => 10);
      if (top) {
        const author = usePresence.getState().users.get(top.user_id);
        if (author) {
          setBadge(displayName(author), author.color, e.clientX, e.clientY, top.user_id);
        } else {
          // 离线作者也有名字：先出缓存；停留 200ms 才发查询（快速扫过不轰炸接口）
          const cached = getCachedUserName(top.user_id);
          if (cached) {
            setBadge(cached.name, cached.color, e.clientX, e.clientY, top.user_id);
          } else {
            setBadge(`用户 #${top.user_id.slice(0, 8)}`, '#a98ab9', e.clientX, e.clientY, top.user_id);
            const p = pendingRef.current;
            if (p && p.userId === top.user_id) {
              if (!p.fired && Date.now() - p.at > 200) {
                p.fired = true;
                void fetchUserName(top.user_id).then((r) => {
                  if (r) {
                    setHoveredAuthor((prev) => {
                      // 仍悬停在缓存命中前显示这个 #id 占位的徽章上才替换
                      if (prev && prev.name === `用户 #${top.user_id.slice(0, 8)}`) {
                        return { ...prev, name: r.name, color: r.color };
                      }
                      return prev;
                    });
                  }
                });
              }
            } else {
              pendingRef.current = { userId: top.user_id, at: Date.now(), fired: false };
            }
          }
        }
      } else {
        setHoveredAuthor(null);
      }
    },
    [],
  );

  const clearHover = useCallback(() => setHoveredAuthor(null), []);

  return { hoveredAuthor, updateHover, clearHover };
}
